import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslationFieldService } from './service/translation-field.service';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { BrowserAccessService } from '../../shared/services/browser-access.service';
import { TranslationRequest } from '../../shared/models/translationRequest';
import { AlertComponent } from '../../shared/components/alert/alert.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { AlertService } from '../../shared/services/alert.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationOptionsComponent } from '../translation-options/translation-options.component';
import { TranslationOptionsService } from '../translation-options/service/translation-options.service';
import { RephraseTransferService } from '../../shared/services/rephrase-transfer.service';
import { LanguagesService } from '../../shared/services/languages.service';

@Component({
  selector: 'app-translation-field',
  imports: [
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    AlertComponent,
    CommonModule,
    ClipboardModule,
    TranslateModule,
    TranslationOptionsComponent,
  ],
  templateUrl: './translation-field.component.html',
  styleUrl: './translation-field.component.scss',
})
export class TranslationFieldComponent implements OnInit {
  private readonly translationFieldService = inject(TranslationFieldService);
  private readonly browserCachingService = inject(BrowserAccessService);
  private readonly alertService = inject(AlertService);
  private readonly rephraseTransferService = inject(RephraseTransferService);
  private readonly translate = inject(TranslateService);
  private readonly translationOptions = inject(TranslationOptionsService);
  private readonly languagesService = inject(LanguagesService);

  public sourceLanguages = this.languagesService.sourceLangs$;
  public targetLanguages = this.languagesService.targetLangs$;
  public selectedSourceLanguage = '';
  public selectedTargetLanguage = '';
  public textToTranslate = '';
  public translatedtext = '';
  public EnterTextToTranslate = '';
  public isTranslated = false;

  ngOnInit() {
    this.CheckForDefaultSourceLanguage();
    this.CheckForDefaultTargetLanguage();
  }

  translateString() {
    if (this.selectedSourceLanguage === '' || this.selectedTargetLanguage === '') {
      this.alertService.showAlert(
        'error',
        this.translate.instant('Alert.SelectLanguageAlertMessage'),
      );
      return;
    }

    this.CheckSourceLanguage();

    if (this.textToTranslate != '') {
      const translationRequest = {
        text: this.textToTranslate,
        sourceLanguage: this.selectedSourceLanguage,
        targetLanguage: this.selectedTargetLanguage,
        formality: this.translationOptions.selectedFormality,
      } as TranslationRequest;

      this.translationFieldService.translateString(translationRequest).subscribe({
        next: (response) => {
          this.translatedtext = response.text;
        },
        error: () => {
          this.alertService.showAlert(
            'error',
            this.translate.instant('Alert.TranslateErrorMessage'),
          );
        },
      });

      this.browserCachingService.setItem('defaultLanguage', this.selectedSourceLanguage.toString());
      this.browserCachingService.setItem(
        'defaultTargetLanguage',
        this.selectedTargetLanguage.toString(),
      );
    }
  }

  private CheckSourceLanguage() {
    if (this.selectedSourceLanguage === 'en-gb' || this.selectedSourceLanguage === 'en-us') {
      this.selectedSourceLanguage = 'en';
    }
  }

  private CheckTargetLanguage() {
    if (this.selectedTargetLanguage === 'en') {
      this.selectedTargetLanguage = 'en-us';
    }
  }

  public SwapTranslatedText() {
    this.SwapLanguage();

    if (this.translatedtext != '' && this.textToTranslate != '') {
      this.textToTranslate = this.translatedtext;
      this.translatedtext = '';

      this.translateString();
    }
  }

  private SwapLanguage() {
    const stepBetween = this.selectedSourceLanguage;
    this.selectedSourceLanguage = this.selectedTargetLanguage;
    this.selectedTargetLanguage = stepBetween;
    this.CheckSourceLanguage();
    this.CheckTargetLanguage();
  }

  copyToClipboard() {
    navigator.clipboard
      .writeText(this.translatedtext)
      .then(() => {
        this.alertService.showAlert(
          'success',
          this.translate.instant('Alert.CopiedToClipboardAlertMessage'),
        );
      })
      .catch(() => {
        this.alertService.showAlert('error', this.translate.instant('Alert.ClipboardErrorMessage'));
      });
  }

  public transferToRephrase() {
    this.rephraseTransferService.transferToRephrase(
      this.translatedtext,
      this.selectedTargetLanguage,
    );
  }

  public CheckForDefaultSourceLanguage() {
    const defaultLanguage = this.browserCachingService.getItem('defaultLanguage');
    console.log(`default language is ${defaultLanguage}`);

    if (defaultLanguage === 'undefined') {
      this.browserCachingService.removeItem('defaultLanguage');
    }
    this.selectedSourceLanguage = defaultLanguage;
  }

  public CheckForDefaultTargetLanguage() {
    const defaultTargetLanguage = this.browserCachingService.getItem('defaultTargetLanguage');
    console.log(`default target language is ${defaultTargetLanguage}`);

    if (defaultTargetLanguage === 'undefined') {
      this.browserCachingService.removeItem('defaultTargetLanguage');
    }
    this.selectedTargetLanguage = defaultTargetLanguage;
  }
}
